import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Table, EmptyTableText } from '@commonClient2/components';
import { toastr } from 'react-redux-toastr';
import {
    DialogTitle,
    DialogContent,
    DialogContentText,
} from '@material-ui/core';
import NotificationTemplatesEdit from './NotificationTemplatesEdit';
import * as notificationService from '../services/api-endpoints/notification.service';
import { extractErrorMessage } from '../../services/api';
import { NotificationTemplateType } from '../../Enums';
import { refreshNotificationTemplates } from '../store/appData';
import './NotificationTemplates.scss';

export const NOTIFICATION_TEMPLATES_ROUTE = '/notificationTemplates';

// Column definitions for the table
const columns = [
    {
        key: 'name',
        tKey: 'columns.notificationTemplate.name',
        sortable: true,
        flexGrow: 2,
    },
    {
        key: 'description',
        tKey: 'columns.notificationTemplate.description',
        sortable: true,
        flexGrow: 2,
    },
    {
        key: 'type',
        tKey: 'columns.notificationTemplate.type',
        sortable: true,
        flexGrow: 1,
    },
    {
        key: 'templateSubject',
        tKey: 'columns.notificationTemplate.templateSubject',
        sortable: true,
        flexGrow: 2,
    },
    {
        key: 'templateContent',
        tKey: 'columns.notificationTemplate.templateContent',
        sortable: false,
        flexGrow: 3,
    },
    {
        key: 'isAvailableForUse',
        tKey: 'columns.notificationTemplate.isAvailableForUse',
        sortable: true,
        flexGrow: 1,
    },
];

const NotificationTemplates = () => {
    const { t } = useTranslation('notificationTemplates');
    const dispatch = useDispatch();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sidePanelOpen, setSidePanelOpen] = useState(false);
    const [sidePanelMode, setSidePanelMode] = useState('create');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const loadingRef = useRef(false);

    /**
     * Load all notification templates from the API
     */
    const loadTemplates = useCallback(async () => {
        // Prevent multiple simultaneous calls
        if (loadingRef.current) {
            return;
        }

        loadingRef.current = true;
        setLoading(true);
        try {
            const response = await notificationService.getAllNotificationTemplates();
            // Backend returns paginated response with itemList
            const templateList = response.data?.itemList || response.data || [];
            const transformedData = templateList.map((template) => {
                const transformed = {
                    id: template.id,
                    name: template.name,
                    description: template.description,
                    type: template.type,
                    templateSubject: template.templateSubject,
                    templateContent: template.isFile ? t('columns.downloadedFile') : template.templateContent,
                    isAvailableForUse: template.isAvailableForUse,
                    isCurrentlyUsed: template.isCurrentlyUsed,
                    isFile: template.isFile,
                    languageCode: template.languageCode,
                    versionName: template.versionName,
                };
                // Store raw data for editing (non-enumerable to exclude from search)
                Object.defineProperty(transformed, '_raw', {
                    value: template,
                    enumerable: false,
                    writable: true,
                    configurable: true
                });
                return transformed;
            });
            setTemplates(transformedData);
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            toastr.error(t('error.loadFailed'), errorMessage);
        } finally {
            setLoading(false);
            loadingRef.current = false;
        }
    }, [t]);

    // Load templates on component mount
    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    /**
     * @param {*} rowData
     */
    const handleEdit = (rowData) => {
        setSelectedTemplate(rowData);
        setSidePanelMode('edit');
        setSidePanelOpen(true);
    };

    /**
     * @param {*} rowData
     */
    const handleDuplicate = (rowData) => {
        setSelectedTemplate(rowData);
        setSidePanelMode('duplicate');
        setSidePanelOpen(true);
    };

    /**
     * @param {*} rowData
     */
    const handleDelete = (rowData) => {
        const toastrConfirmOptions = {
            onOk: async () => {
                try {
                    await notificationService.deleteNotificationTemplate(rowData.id);
                    toastr.success(t('delete.successTitle'), t('delete.successMessage', { name: rowData.name }));
                    loadTemplates(); // Reload the list
                    dispatch(refreshNotificationTemplates()); // Refresh Redux store so bid setup form has latest templates
                } catch (error) {
                    const errorMessage = extractErrorMessage(error);
                    toastr.error(t('error.deleteFailed'), errorMessage);
                }
            },
            onCancel: () => { },
            component: () => (
                <div>
                    <DialogTitle style={{ padding: '16px 24px' }}>
                        {t('delete.title')}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {t('delete.contentText', { name: rowData.name })}
                        </DialogContentText>
                    </DialogContent>
                </div>
            ),
        };
        toastr.confirm('', toastrConfirmOptions);
    };

    const handleCreateNew = () => {
        setSelectedTemplate(null);
        setSidePanelMode('create');
        setSidePanelOpen(true);
    };

    /**
     * @param {*} template
     * @param {string} mode
     */
    const handleSave = async (template, mode) => {
        try {
            if (mode === 'create') {
                await notificationService.createNotificationTemplate(template);
                toastr.success(t('save.successTitle'), t('save.createSuccessMessage', { name: template.name }));
            } else if (mode === 'edit') {
                await notificationService.updateNotificationTemplate(template.id, template);
                toastr.success(t('save.successTitle'), t('save.updateSuccessMessage', { name: template.name }));
            } else if (mode === 'duplicate') {
                // Convert type enum number to string name for backend
                const getTypeString = (typeValue) => {
                    switch (typeValue) {
                        case NotificationTemplateType.Email:
                            return 'Email';
                        case NotificationTemplateType.Sms:
                            return 'Sms';
                        case NotificationTemplateType.InApp:
                            return 'InApp';
                        default:
                            return 'Email';
                    }
                };

                await notificationService.duplicateNotificationTemplate(template.sourceId, {
                    newName: template.name,
                    description: template.description,
                    templateSubject: template.templateSubject,
                    templateContent: template.templateContent,
                    type: getTypeString(template.type),
                    languageCode: template.languageCode,
                    isAvailableForUse: template.isAvailableForUse,
                    isFile: template.isFile,
                });
                toastr.success(t('save.successTitle'), t('save.duplicateSuccessMessage', { name: template.name }));
            }
            loadTemplates(); // Reload the list
            dispatch(refreshNotificationTemplates()); // Refresh Redux store so bid setup form has latest templates
            setSidePanelOpen(false);
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            toastr.error(t('error.saveFailed'), errorMessage);
            throw error; // Re-throw to let the side panel handle it
        }
    };

    /**
     * Custom renderer for templateContent column to handle file vs text
     * @param {*} rowData
     */
    const renderTemplateContent = (rowData) => {
        if (rowData.isFile) {
            return <span className="file-indicator">{t('columns.downloadedFile')}</span>;
        }
        return <span className="template-content-preview">{rowData.templateContent}</span>;
    };

    /**
     * Custom renderer for type column to display translated label
     * @param {*} rowData
     */
    const renderType = (rowData) => {
        const { type } = rowData;
        switch (type) {
            case NotificationTemplateType.Email:
                return t('types.Email');
            case NotificationTemplateType.Sms:
                return t('types.Sms');
            case NotificationTemplateType.InApp:
                return t('types.InApp');
            default:
                return type;
        }
    };

    /**
     * Custom renderer for isAvailableForUse column to display as badge
     * @param {*} rowData
     */
    const renderAvailability = (rowData) => {
        const isAvailable = rowData.isAvailableForUse;
        return (
            <span className={`availability-badge ${isAvailable ? 'available' : 'not-available'}`}>
                {isAvailable ? t('columns.available') : t('columns.notAvailable')}
            </span>
        );
    };

    return (
        <div className="notification-templates-full-page">
            <Table
                loading={loading}
                data={templates}
                columns={columns}
                customColumnRenderers={[
                    {
                        columnName: 'type',
                        renderCell: renderType
                    },
                    {
                        columnName: 'templateContent',
                        renderCell: renderTemplateContent
                    },
                    {
                        columnName: 'isAvailableForUse',
                        renderCell: renderAvailability
                    }
                ]}
                id="notification-templates-table"
                renderEmpty={() => <EmptyTableText text={t('noTemplatesFound')} />}
                onEdit={(_, value) => handleEdit(value)}
                onCreate={handleCreateNew}
                createLabel={t('createButton')}
                onDuplicate={(_, value) => handleDuplicate(value)}
                onDelete={(_, value) => handleDelete(value)}
                deleteCondition={(rowData) => !rowData.isCurrentlyUsed}
                fillAvailableHeight
                defaultSortColumnKey="name"
            />
            <NotificationTemplatesEdit
                open={sidePanelOpen}
                setOpen={setSidePanelOpen}
                mode={sidePanelMode}
                dataSet={selectedTemplate}
                onSave={handleSave}
            />
        </div>
    );
};

export default NotificationTemplates;
